import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { PAGE_SIZE } from './productListHelper';
import { PlaceholderObserver } from './list-view/PlaceholderObserver';
import { ListViewPlaceHolder } from './list-view/ListViewPlaceHolder';

export const ProductLoader = ({
  renderGroupContent,
  group,
  groupItems,
  groupChangeStatus,
  groupIndex,
  loadItemsOnDemand,
  isExpandAllInProgress,
  setLoadingItemIds,
  onObserverIntersect
}) => {
  const { notLoadedItems, loadedItemsCount = 0 } = group || {};
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  const onLoadMoreItems = !isExpandAllInProgress && loadItemsOnDemand ? async ({ groupId: targetGroupId }) => {
    if (targetGroupId === group?.id && notLoadedItems?.length > 0) {
      const itemsToLoad = notLoadedItems.slice(0, PAGE_SIZE);

      if (setLoadingItemIds) {
        setLoadingItemIds((prev) => {
          const newSet = new Set(prev);
          itemsToLoad.forEach((itemId) => newSet.add(itemId));
          return newSet;
        });
      }

      try {
        await loadItemsOnDemand(itemsToLoad);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading items on scroll:', error);
      } finally {
        if (setLoadingItemIds) {
          setLoadingItemIds((prev) => {
            const newSet = new Set(prev);
            itemsToLoad.forEach((itemId) => newSet.delete(itemId));
            return newSet;
          });
        }
      }
    }
  } : null;

  const totalPlaceholders = notLoadedItems?.length || 0;

  const placeholderElements = (notLoadedItems || []).map((itemId, i) => {
    // Calculate actual position in original list by adding offset of already loaded items
    const actualIndex = loadedItemsCount + i;

    return (
      <PlaceholderObserver
        key={`placeholder-${group?.id}-${itemId}`}
        observerIndex={actualIndex}
        totalPlaceholders={totalPlaceholders}
        onIntersect={onObserverIntersect}
        groupId={group?.id}
      >
        <ListViewPlaceHolder count={1} isMobile={isMobile} />
      </PlaceholderObserver>
    );
  });

  return (
    <>
      {renderGroupContent({
        groupItems,
        group,
        loadMore: onLoadMoreItems,
        groupChangeStatus,
        groupIndex
      })}
      {placeholderElements}
    </>
  );
};

ProductLoader.displayName = 'ProductLoader';

ProductLoader.propTypes = {
  renderGroupContent: PropTypes.func.isRequired,
  group: PropTypes.shape({
    id: PropTypes.string,
    notLoadedItems: PropTypes.arrayOf(PropTypes.string),
    loadedItemsCount: PropTypes.number
  }),
  groupItems: PropTypes.arrayOf(PropTypes.string).isRequired,
  groupChangeStatus: PropTypes.arrayOf(PropTypes.bool).isRequired,
  groupIndex: PropTypes.number.isRequired,
  loadItemsOnDemand: PropTypes.func,
  isExpandAllInProgress: PropTypes.bool,
  setLoadingItemIds: PropTypes.func,
  onObserverIntersect: PropTypes.func
};

ProductLoader.defaultProps = {
  group: null,
  loadItemsOnDemand: null,
  isExpandAllInProgress: false,
  setLoadingItemIds: null,
  onObserverIntersect: null
};
